import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  emailOnlySchema,
  registrationSchema,
  type EmailOnlyValues,
  type RegistrationValues,
} from '../../lib/emailSchema'
import styles from './EmailStep.module.css'

/** Правовые документы (Яндекс.Диск) для текста согласия при регистрации. */
const LEGAL_DOCS = {
  privacy: 'https://disk.yandex.ru/i/TmehyTl8B7K_dA',
  personalData: 'https://disk.yandex.ru/i/0vdLLbMIoLqHeg',
  ads: 'https://disk.yandex.ru/i/SP4HBBdWOip9_Q',
  offer: 'https://disk.yandex.ru/i/7de-MUj6R2F2zQ',
} as const

interface EmailStepProps {
  /** Стартовое значение поля email */
  initialEmail?: string
  /** Заголовок формы */
  title?: string
  /** Пояснительная сноска под заголовком (опционально) */
  description?: string
  /** Текст кнопки отправки в режиме логина */
  submitLabel?: string
  /**
   * Обработчик отправки.
   * @param email - валидный нормализованный email
   * @param allowRegistration - true если юзер согласился на регистрацию
   */
  onSubmit: (email: string, allowRegistration?: boolean) => Promise<void>
  /** Индикатор процесса отправки */
  isSubmitting?: boolean
  /** Общая ошибка под формой (от сервера) */
  errorText?: string | null
  /** Режим регистрации: показывает 3 чекбокса согласий */
  needsRegistration?: boolean
}

/**
 * Шаг ввода email. По умолчанию — логин (только email).
 * Если бэк вернул «Необходима регистрация» — переключаем в режим регистрации
 * с 3 чекбоксами согласий (оферта и ПД обязательные, реклама — нет).
 */
export const EmailStep = ({
  initialEmail = '',
  title = 'Войти через почту',
  description,
  submitLabel = 'Получить код',
  onSubmit,
  isSubmitting = false,
  errorText = null,
  needsRegistration = false,
}: EmailStepProps) => {
  type Values = RegistrationValues | EmailOnlyValues

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<Values>({
    resolver: yupResolver(
      needsRegistration ? registrationSchema : emailOnlySchema
    ) as never,
    mode: 'onChange',
    defaultValues: needsRegistration
      ? { email: initialEmail, agreedOffer: false, agreedPersonalData: false, agreedAds: false }
      : { email: initialEmail },
  })

  const submit = async (values: Values) => {
    await onSubmit(values.email, needsRegistration)
  }

  const emailError = errors.email?.message
  const offerError = 'agreedOffer' in errors ? errors.agreedOffer?.message : undefined
  const personalDataError =
    'agreedPersonalData' in errors ? errors.agreedPersonalData?.message : undefined
  const inputClass = [styles.input, emailError && styles.inputInvalid]
    .filter(Boolean)
    .join(' ')

  const effectiveTitle = needsRegistration ? 'Регистрация' : title
  const effectiveSubmitLabel = needsRegistration ? 'Зарегистрироваться' : submitLabel

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.form} noValidate>
      <div className={styles.header}>
        {description ? (
          <div className={styles.titleGroup}>
            <h1 className={styles.title}>{effectiveTitle}</h1>
            <p className={styles.description}>{description}</p>
          </div>
        ) : (
          <h1 className={styles.title}>{effectiveTitle}</h1>
        )}

        <div className={styles.fields}>
          <input
            id="auth-email"
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="Email"
            className={inputClass}
            {...register('email')}
          />

          {emailError && <span className={styles.error}>{emailError}</span>}

          {needsRegistration && (
            <div className={styles.agreements}>
              <label className={styles.agreement}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  {...register('agreedOffer' as never)}
                />
                <span className={styles.agreementText}>
                  Я согласен с условиями{' '}
                  <a
                    href={LEGAL_DOCS.offer}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    публичной оферты о заключении договора оказания
                    информационно-консультационных услуг
                  </a>
                </span>
              </label>

              <label className={styles.agreement}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  {...register('agreedPersonalData' as never)}
                />
                <span className={styles.agreementText}>
                  Я даю согласие на{' '}
                  <a
                    href={LEGAL_DOCS.personalData}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    обработку своих персональных данных
                  </a>
                  {' в соответствии с '}
                  <a
                    href={LEGAL_DOCS.privacy}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    политикой
                  </a>
                </span>
              </label>

              <label className={styles.agreement}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  {...register('agreedAds' as never)}
                />
                <span className={styles.agreementText}>
                  Я даю согласие на получение{' '}
                  <a
                    href={LEGAL_DOCS.ads}
                    className={styles.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    рекламной и информационной рассылки
                  </a>
                </span>
              </label>
            </div>
          )}

          {offerError && <span className={styles.error}>{offerError}</span>}
          {personalDataError && <span className={styles.error}>{personalDataError}</span>}
          {errorText && <span className={styles.error}>{errorText}</span>}
        </div>
      </div>

      <button
        type="submit"
        disabled={!isValid || isSubmitting}
        className={styles.submit}
      >
        {isSubmitting ? 'Отправляем…' : effectiveSubmitLabel}
      </button>
    </form>
  )
}
